import React, { forwardRef } from 'react';
import { Box, Checkbox, Chip, ListItemText, MenuItem, OutlinedInput, Select } from '@mui/material';
import type { SelectChangeEvent, SelectProps } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { FieldBaseProps, SelectOption } from './field.types';
import { FieldShell, describedBy } from './FieldShell';
import { useFieldIds } from './useFieldIds';
import { controlTextSx, placeholderSx } from './fieldStyles';

export interface MultiSelectInputProps<T extends string | number> extends FieldBaseProps {
  options: readonly SelectOption<T>[];
  value: readonly T[];
  onChange: (value: T[], event: SelectChangeEvent<T[]>) => void;
  placeholder?: React.ReactNode;
  maxVisibleChips?: number;
  name?: string;
  readOnly?: boolean;
  MenuProps?: SelectProps['MenuProps'];
}

const optionCheckboxSx = { p: 0, marginInlineEnd: 2 } as const;

function MultiSelectInputInner<T extends string | number>(props: MultiSelectInputProps<T>, ref: React.ForwardedRef<HTMLDivElement>) {
  const {
    id: idProp, label, hideLabel, helperText, describedById, error, required, hideRequiredMark, disabled, size = 'small', fullWidth = true, className, sx,
    options, value, onChange, placeholder, maxVisibleChips = 3, name, readOnly, MenuProps,
  } = props;
  const { t } = useTranslation();
  const ids = useFieldIds(idProp);
  const labelOf = (v: T) => options.find((o) => o.value === v)?.label ?? String(v);
  const selectedCount = (count: number): string =>
    count === 1 ? t('coreComponents.fields.nSelectedOne', '1 selected') : t('coreComponents.fields.nSelected', '{{count}} selected', { count });

  return (
    <FieldShell ref={ref} ids={ids} label={label} hideLabel={hideLabel} helperText={helperText} error={error} required={required} hideRequiredMark={hideRequiredMark} disabled={disabled} size={size} fullWidth={fullWidth} className={className} sx={sx}>
      <Select<T[]>
        id={ids.id}
        labelId={ids.labelId}
        name={name}
        readOnly={readOnly}
        multiple
        displayEmpty
        value={value as T[]}
        onChange={(event) => {
          const raw = event.target.value;
          onChange(Array.isArray(raw) ? raw : [], event);
        }}
        input={<OutlinedInput notched={false} sx={controlTextSx(size)} />}
        SelectDisplayProps={{ 'aria-describedby': describedBy(ids, helperText, describedById), 'aria-invalid': error || undefined }}
        MenuProps={MenuProps}
        renderValue={(selected) => {
          if (selected.length === 0) return <Box component="span" sx={placeholderSx}>{placeholder ?? ''}</Box>;
          if (selected.length > maxVisibleChips) return selectedCount(selected.length);
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selected.map((v) => (
                <Chip key={String(v)} size="small" label={labelOf(v)} />
              ))}
            </Box>
          );
        }}
      >
        {options.map((o) => (
          <MenuItem key={String(o.value)} value={o.value} disabled={o.disabled} dense>
            <Checkbox size="small" checked={value.includes(o.value)} tabIndex={-1} disableRipple sx={optionCheckboxSx} />
            <ListItemText primary={o.label} secondary={o.description} />
          </MenuItem>
        ))}
      </Select>
    </FieldShell>
  );
}

export const MultiSelectInput = forwardRef(MultiSelectInputInner) as <T extends string | number>(
  props: MultiSelectInputProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;
